import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Bot, Cake, GraduationCap, CheckCircle2, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONTENT = {
  hero: { title: 'Our Programs', subtitle: 'Discover the perfect learning path for your child' },
  programs: [
    { id: 'robotics', title: 'Robotics Teams', description: 'Build, program, and compete with robots.', icon: 'Bot', link: '/programs/robotics' }
  ]
};

const ICON_MAP = { Bot, Cake, GraduationCap, BookOpen };
const COLOR_MAP = { robotics: 'from-blue-500 to-cyan-500' };

// Static program details (for individual program pages)
const programDetails = {
  robotics: { features: ['Hands-on robot building', 'Learn programming fundamentals', 'Compete in competitions', 'Develop teamwork skills'], ages: '8-18 years', schedule: 'Weekly practices + competitions', hero: 'https://images.pexels.com/photos/7869138/pexels-photo-7869138.jpeg?w=1920' }
};

export default function ProgramsPage() {
  const { program } = useParams();
  const [searchParams] = useSearchParams();
  const content = DEFAULT_CONTENT;
  const getIcon = (name) => ICON_MAP[name] || BookOpen;

  // Individual program detail view
  if (program && programDetails[program]) {
    const progData = (content.programs || DEFAULT_CONTENT.programs).find(p => p.id === program) || {};
    const details = programDetails[program];
    const Icon = getIcon(progData.icon);

    // Determine CTA based on program type
    const getCTA = () => {
      return { text: t('pages.programs.findClasses'), link: '/catalog', secondaryText: t('pages.programs.contactUs'), secondaryLink: '/contact' };
    };
    const cta = getCTA();

    return (
      <div data-testid={`program-${program}-page`}>
        
        <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${details.hero})` }} />
          <div className="container-app relative z-10">
            <div className="max-w-3xl">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${COLOR_MAP[program] || 'from-blue-500 to-cyan-500'} flex items-center justify-center mb-6`}>
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">{progData.title || program}</h1>
              <p className="text-xl text-white/80 mb-8">{progData.description}</p>
              <div className="flex flex-wrap gap-4">
                <Link to={cta.link}><Button className="btn-secondary text-lg px-8 py-6">{cta.text} <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
                <Link to={cta.secondaryLink}><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">{cta.secondaryText}</Button></Link>
              </div>
            </div>
          </div>
        </section>
        <section className="section-padding">
          <div className="container-app">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8">{t('pages.programs.whatsIncluded')}</h2>
                <div className="space-y-4">
                  {details.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" /><span className="text-lg">{feature}</span></div>
                  ))}
                </div>
              </div>
              <Card className="card-dashboard">
                <CardContent className="p-8">
                  <h3 className="font-heading text-2xl font-bold mb-6">{t('pages.programs.programDetails')}</h3>
                  <div className="space-y-4">
                    <div><p className="text-sm text-muted-foreground">{t('pages.programs.ages')}</p><p className="font-semibold">{details.ages}</p></div>
                    <div><p className="text-sm text-muted-foreground">{t('pages.programs.schedule')}</p><p className="font-semibold">{details.schedule}</p></div>
                  </div>
                  <div className="mt-8 space-y-3">
                    <Link to={cta.link}><Button className="btn-primary w-full">{cta.text}</Button></Link>
                    <Link to={cta.secondaryLink}><Button variant="outline" className="w-full">{cta.secondaryText}</Button></Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Programs overview page
  return (
    <div data-testid="programs-page">
      
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">{content.hero?.title || 'Our Programs'}</h1>
            <p className="text-xl text-white/80">{content.hero?.subtitle || ''}</p>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-app">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(content.programs || DEFAULT_CONTENT.programs).map((prog, index) => {
              const Icon = getIcon(prog.icon);
              const colorClass = COLOR_MAP[prog.id] || 'from-blue-500 to-cyan-500';
              return (
                <Link key={prog.id || index} to={prog.link || `/programs/${prog.id}`} className="group" data-testid={`program-${prog.id}`}>
                  <Card className="card-marketing h-full overflow-hidden">
                    <CardContent className="p-8">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center mb-4`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold mb-3">{prog.title}</h3>
                      <p className="text-muted-foreground mb-4">{prog.description}</p>
                      <span className="inline-flex items-center text-primary font-medium group-hover:gap-3 gap-2 transition-all">Learn More <ArrowRight className="w-4 h-4" /></span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-muted/50">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">{t('pages.programs.notSure')}</h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">{t('pages.programs.notSureDesc')}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/catalog"><Button className="btn-primary text-lg px-8 py-6">{t('pages.programs.browseAll')} <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/contact"><Button variant="outline" className="text-lg px-8 py-6">{t('pages.programs.contactHelp')}</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
